import { Button, Form, Input, InputNumber, Select, Space, Switch, message } from "antd";
import AlarmRuleApi from "../../../api/AlarmRuleApi";
import DeviceTemplateApi from "../../../api/DeviceTemplateApi";
import DeviceApi from "../../../api/DeviceApi";
import React, { useEffect, useState } from "react";
import { CreateAlarmRuleWizardWrapper, EditFormLayout, EditFormRow, FormWrapper, OperationWrapper, Title, TitleWrapper } from "./CreateAlarmRuleWizard.styled";
import StringUtils from "../../../util/StringUtils";

interface CreateAlarmRuleWizardProps {
    alarmRuleId: string;
    onClose: ()=> void,
    onComplete: ()=> void
}

const CreateAlarmRuleWizard: React.FC<CreateAlarmRuleWizardProps> = (props)=> {
    // api
    let alarmRuleApi = new AlarmRuleApi();
    let deviceTemplateApi = new DeviceTemplateApi();
    let deviceApi = new DeviceApi();
    const [messageApi, contextHolder] = message.useMessage();
    
    // state
    let [alarmRule, setAlarmRule] = useState<any>({
        name: '',
        type: 'DEVICE_TEMPLATE',
        expression: '',
        remarks: '',
        enabled: true,
    });
    let [deviceTemplates, setDeviceTemplates] = useState([]);
    let [devices, setDevices] = useState([]);
    let [metricKeyOptions, setMetricKeyOptions] = useState([]);
    let [submitEnabled, setSubmitEnabled] = useState(false);
    const [form] = Form.useForm();
    
    // options
    let typeOptions = [
        { label: '设备模板', value: 'DEVICE_TEMPLATE' },
        { label: '设备', value: 'DEVICE' }
    ];

    let operatorOptions = [
        { label: '大于', value: 'GT' },
        { label: '大于或等于', value: 'GTE' },
        { label: '小于', value: 'LT' },
        { label: '小于或等于', value: 'LTE' },
        { label: '等于', value: 'EQ' },
        { label: '自定义', value: 'CUSTOMIZED' },
    ];

    let deviceTemplateOptions = deviceTemplates.map(item => ({
        label: item.name,
        value: item.id
    }));

    let deviceOptions = devices.map(item => ({
        label: item.name,
        value: item.id
    }));

    // events
    let onNameChange = (e)=> {
        alarmRule.name = e.target.value;
        setAlarmRule({...alarmRule});
    };

    let onTypeChange = (value)=> {
        alarmRule.type = value;
        setAlarmRule({...alarmRule});
    };

    let onDeviceTemplateIdChange = (value)=> {
        alarmRule.deviceTemplateId = value;
        setAlarmRule({...alarmRule});
    };

    let onDeviceIdChange = (value)=> {
        alarmRule.deviceId = value;
        setAlarmRule({...alarmRule});

        let device = devices.find(item => item.id === value);
        alarmRule.deviceTemplateId = device.deviceTemplateId;
        setAlarmRule({...alarmRule});
    };

    let onMetricKeyChange = (value)=> {
        alarmRule.metricKey = value;
        setAlarmRule({...alarmRule});
    };

    let onOperatorChange = (value)=> {
        alarmRule.operator = value;
        setAlarmRule({...alarmRule});
    };

    let onThresholdChange = (value)=> {
        alarmRule.threshold = value;
        setAlarmRule({...alarmRule});
    };

    let onExpressionChange = (e)=> {
        alarmRule.expression = e.target.value;
        setAlarmRule({...alarmRule});
    };


    let onRemarksChange = (e)=> {
        alarmRule.remarks = e.target.value;
        setAlarmRule({...alarmRule});
    };

    let onEnabledChange = (checked)=> {
        alarmRule.enabled = checked;
        setAlarmRule({...alarmRule});
    };

    // methods
    let loadDeviceTemplates = async()=> {
        let deviceTemplates = (await deviceTemplateApi.getAll()).data.data;
        setDeviceTemplates(deviceTemplates);
    };

    let loadDevices = async()=> {
        let devices = (await deviceApi.getAll()).data.data;
        setDevices(devices);
    };

    let loadAlarmRule = async()=> {
        if (props.alarmRuleId) {
            let alarmRule = (await alarmRuleApi.get(props.alarmRuleId)).data.data;
            setAlarmRule(alarmRule);
            form.setFieldsValue(alarmRule);
        }
    };

    let onSubmit = async ()=> {
        if (StringUtils.isEmpty(props.alarmRuleId)) {
            await alarmRuleApi.create(alarmRule);
        } else {
            await alarmRuleApi.update(alarmRule);
        }
        message.success('保存成功！');
        props.onComplete();
    };

    let onCancel = ()=> {
        props.onClose();
    };

    let validateForm = ()=> {
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            setSubmitEnabled(false);
        });
    }

    // onload
    useEffect(()=> {
        loadDeviceTemplates();
        loadDevices();
        loadAlarmRule();
    }, []);

    useEffect(()=> {
        validateForm();
    }, [alarmRule]);

    let refreshMetricKeyOptions = ()=> {
        let deviceTemplateId = StringUtils.nonull(alarmRule.deviceTemplateId);
        if (StringUtils.isEmpty(deviceTemplateId)) {
            setMetricKeyOptions([]);
            return;
        }
        let deviceTemplate = deviceTemplates.find(item => item.id == deviceTemplateId);
        if (deviceTemplate == null) {
            setMetricKeyOptions([]);
            return;
        }

        let deviceTemplateConfiguration = JSON.parse(deviceTemplate.configuration);
        let properties = deviceTemplateConfiguration.properties;
        let metricKeyOptions = properties.map((property)=> {
            return {
                label: `${property.name} (${property.unit})`,
                value: property.key
            }
        });
        setMetricKeyOptions(metricKeyOptions);
    };

    useEffect(()=> {
        refreshMetricKeyOptions();
    }, [alarmRule.deviceTemplateId]);

    // render
    return <CreateAlarmRuleWizardWrapper>
        <TitleWrapper>
            <Title>{ props.alarmRuleId ? '编辑规则' : '新建规则' }</Title>
            {contextHolder}
        </TitleWrapper>

        <FormWrapper>
            <Form form={form} name="alarm-rule" layout="vertical">
                <EditFormLayout>
                    <EditFormRow>
                        <Form.Item name="name" label="规则名称" rules={[{required: true}]}>
                            <Input
                                placeholder="请输入规则名称"
                                value={alarmRule.name}
                                onChange={onNameChange}
                            />
                        </Form.Item>

                        <Form.Item name="type" label="规则类型" rules={[{required: true}]}>
                            <Select
                                placeholder="请选择规则类型"
                                value={alarmRule.type}
                                onChange={onTypeChange}
                                options={typeOptions}
                            />
                        </Form.Item>
                    </EditFormRow>

                    <EditFormRow>
                        { alarmRule.type == 'DEVICE_TEMPLATE' && <Form.Item name="deviceTemplateId" label="设备模板" rules={[{required: alarmRule.type == 'DEVICE_TEMPLATE'}]}>
                            <Select 
                                placeholder="请选择设备模板" 
                                value={alarmRule.deviceTemplateId} 
                                onChange={onDeviceTemplateIdChange} 
                                options={deviceTemplateOptions} />
                        </Form.Item> }

                        { alarmRule.type == 'DEVICE' && <Form.Item name="deviceId" label="设备" rules={[{required: alarmRule.type == 'DEVICE'}]}>
                            <Select 
                                placeholder="请选择目标设备" 
                                value={alarmRule.deviceId} 
                                onChange={onDeviceIdChange}
                                options={deviceOptions} />
                        </Form.Item> }

                        <Form.Item name="metricKey" label="变量名称" rules={[{required: true}]}>
                            <Select 
                                placeholder="请选择变量名称" 
                                value={alarmRule.metricKey} 
                                onChange={onMetricKeyChange}
                                options={metricKeyOptions} />
                        </Form.Item>

                        <Form.Item name="operator" label="条件" rules={[{required: true}]}>
                            <Select 
                                placeholder="请选择条件类型" 
                                value={alarmRule.operator} 
                                onChange={onOperatorChange}
                                options={operatorOptions} />
                        </Form.Item>

                        <Form.Item name="threshold" label="阈值" rules={[{required: alarmRule.operator != 'CUSTOMIZED'}]}>
                            <InputNumber
                                disabled={alarmRule.operator == 'CUSTOMIZED'}
                                placeholder="请输入阈值" 
                                onChange={onThresholdChange}
                                value={alarmRule.threshold}
                                style={{width: '100%'}}
                            />
                        </Form.Item>
                    </EditFormRow>
                    
                    <EditFormRow>
                        <Form.Item name="expression" label="自定义条件表达式（返回true则触发告警）" rules={[{required: alarmRule.operator == 'CUSTOMIZED'}]}>
                            <Input.TextArea 
                                disabled={alarmRule.operator != 'CUSTOMIZED'}
                                style={{height: '80px'}} 
                                onChange={onExpressionChange} 
                                placeholder="自定义条件表达式"/>
                        </Form.Item>
                    </EditFormRow>

                    <EditFormRow>
                        <Form.Item name="remarks" label="备注" rules={[{required: false}]}>
                            <Input.TextArea style={{height: '80px'}} onChange={onRemarksChange} placeholder="请输入备注"/>
                        </Form.Item>
                    </EditFormRow>

                    <EditFormRow>
                        <Form.Item name="enabled" label="启用">
                            <Switch checked={alarmRule.enabled} onChange={onEnabledChange} />
                        </Form.Item>
                    </EditFormRow>
                </EditFormLayout>
            </Form>
        </FormWrapper>

        <OperationWrapper>
            <Space size="middle">
                <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                <Button type="primary" danger onClick={onCancel}>取消</Button>
            </Space>
        </OperationWrapper>
    </CreateAlarmRuleWizardWrapper>
};

export default CreateAlarmRuleWizard;